import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import Shell from "@/components/Shell";
import { http } from "@/lib/api";
import { useApp } from "@/lib/store";
import { track } from "@/lib/analytics";

const PERKS = [
  { title: "Your Pattern Review", body: "The full 12-section reading of which states recur, what tends to trigger them, and what has actually helped." },
  { title: "Download as PDF", body: "Save or share your Pattern Review as a clean, private document." },
  { title: "History across devices", body: "Your check-ins follow you everywhere you sign in." },
  { title: "Gentle reminders", body: "Optional, off by default — a quiet nudge to check in. (Coming soon)" },
];

export default function Plus() {
  const nav = useNavigate();
  const { user, isPlus, checkoutPlus } = useApp();
  const [plans, setPlans] = useState(null);
  const [interval, setInterval] = useState("yearly");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    track("plus_viewed");
    http.get("/billing/plans").then(({ data }) => setPlans(data)).catch(() => {});
  }, []);

  const upgrade = async () => {
    if (!user) { nav("/login"); return; }
    setBusy(true);
    track("trial_started", { interval });
    try {
      await checkoutPlus(interval === "yearly" ? "plus_yearly" : "plus_monthly");
    } catch (e) {
      toast("Could not start checkout. Please try again.");
      setBusy(false);
    }
  };

  const sel = plans ? (interval === "yearly" ? plans.yearly : plans.monthly) : null;

  return (
    <Shell>
      <div className="px-6 pt-12 pb-24">
        <button data-testid="plus-back" onClick={() => nav(-1)}
          className="inline-flex items-center gap-1 text-[13px] text-[#6B6B6B] hover:text-[#1A1A1A] ss-focus -ml-1">
          <ArrowLeft size={18} strokeWidth={1.5} /> Back
        </button>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} className="mt-8">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#F7F2D7] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8a6d10]">
            <Sparkles size={13} strokeWidth={2} /> Signal State ID Plus
          </div>
          <h1 className="mt-5 font-serif-display text-4xl leading-tight">
            See the pattern beneath the moments.
          </h1>
          <p className="mt-4 text-[15px] leading-relaxed text-[#474747]">
            The 60-second check-in is always free. Plus unlocks the deeper reading — how your
            states connect over time, and what has genuinely helped you.
          </p>
        </motion.div>

        {isPlus ? (
          <div className="mt-8 ss-card p-6 text-center" data-testid="plus-already">
            <p className="font-serif-display text-2xl">You're on Plus.</p>
            <p className="mt-2 text-[14px] text-[#474747]">Thank you for supporting Signal State ID.</p>
            <button data-testid="plus-open-pattern" onClick={() => nav("/pattern")}
              className="mt-5 w-full bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-6 py-3.5 font-medium ss-tap ss-focus">
              Open my Pattern Review
            </button>
          </div>
        ) : (
          <>
            <div className="mt-8 grid grid-cols-2 gap-2 p-1 rounded-full bg-[#EFEDE7]" data-testid="plus-interval-toggle">
              {["yearly", "monthly"].map((k) => (
                <button key={k} data-testid={`plus-interval-${k}`} onClick={() => setInterval(k)}
                  className={`rounded-full py-2.5 text-[13.5px] font-medium transition-colors ss-focus ${interval === k ? "bg-[#FDFBF7] text-[#1A1A1A] shadow-sm" : "text-[#6B6B6B]"}`}>
                  {k === "yearly" ? "Annual" : "Monthly"}
                </button>
              ))}
            </div>

            <div className="mt-4 ss-card p-6" data-testid="plus-price-card">
              {sel ? (
                <div className="flex items-end justify-between">
                  <div>
                    <div className="flex items-baseline gap-1">
                      <span className="font-serif-display text-5xl">
                        {interval === "yearly" ? plans.yearly.per_month : plans.monthly.display}
                      </span>
                      <span className="text-[14px] text-[#6B6B6B]">/ month</span>
                    </div>
                    <p className="mt-1 text-[13px] text-[#6B6B6B]">
                      {interval === "yearly"
                        ? `21 days free, then ${plans.yearly.display} billed yearly`
                        : `21 days free, then ${plans.monthly.display} billed monthly`}
                    </p>
                  </div>
                  {interval === "yearly" && (
                    <span className="rounded-full bg-[#E8F0EA] px-3 py-1 text-[12px] font-semibold text-[#2f6b45]">
                      Save {plans.yearly.savings_pct}%
                    </span>
                  )}
                </div>
              ) : (
                <p className="text-[#6B6B6B] font-mono-ss text-sm">Loading plans…</p>
              )}
            </div>

            <button data-testid="plus-upgrade" onClick={upgrade} disabled={busy || !plans}
              className="mt-4 w-full bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-6 py-4 text-base font-medium ss-tap ss-focus hover:-translate-y-0.5 disabled:opacity-60">
              {busy
                ? "Opening secure checkout…"
                : !user
                  ? "Sign in to upgrade"
                  : "Start my 21-day free trial"}
            </button>
            <p className="mt-3 text-center text-[12px] text-[#6B6B6B]">
              Free for 21 days · cancel anytime before it ends · plus applicable tax.
            </p>
          </>
        )}

        <div className="mt-10 space-y-3">
          {PERKS.map((p, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.04 * i }} className="flex gap-3 ss-card p-4">
              <span className="mt-0.5 flex-shrink-0 grid place-items-center h-6 w-6 rounded-full bg-[#F7F2D7]">
                <Check size={14} strokeWidth={2.5} className="text-[#8a6d10]" />
              </span>
              <div>
                <h3 className="text-[14.5px] font-semibold">{p.title}</h3>
                <p className="mt-1 text-[13.5px] leading-relaxed text-[#474747]">{p.body}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <p className="mt-8 text-center text-[12.5px] text-[#6B6B6B] leading-relaxed">
          Your data is never sold or shared. Plus supports the work, it doesn't unlock your privacy.
        </p>
      </div>
    </Shell>
  );
}
